/**
 * Program 12.1 UX2 — Pursuit workspace view model (FE aggregation, no new entity).
 */
import type { Opportunity } from '@/services/opportunity.service';
import type {
  CommercialLifecycleContext,
  CommercialLifecycleDefinition,
} from '@/services/commercialLifecycle.service';
import {
  buildLifecycleStages,
  type LifecycleStageView,
} from '@/components/commercial-lifecycle/lifecycleStageView';
import { epcInvitationKind } from '@/constants/epcInvitationKind';
import { buildEarlyPursuitAttention } from './earlyPursuitAttention';

export type PursuitAttentionItem = {
  id: string;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  detail?: string;
  stageKey?: string;
};

export type PursuitNextAction = {
  id: string;
  label: string;
  toStageKey: string;
  blocked: boolean;
  reason?: string;
  conversionProfileKey?: string | null;
  implementation?: string | null;
};

export type PursuitLineageNode = {
  entityType: string;
  entityId: string;
  stageKey: string;
  revision?: number;
};

export type PursuitWorkspaceViewModel = {
  opportunityId: string;
  opportunity: Opportunity | null;
  context: CommercialLifecycleContext | null;
  stages: LifecycleStageView[];
  attention: PursuitAttentionItem[];
  nextActions: PursuitNextAction[];
  lineage: PursuitLineageNode[];
  primaryTenderId: string | null;
  primaryPrequalId: string | null;
  invitationKind: 'EOI' | 'RFQ' | 'RFT' | null;
  invitationHistory: Array<{
    id: string;
    kind: 'EOI' | 'RFQ' | 'RFT';
    number: string;
    current: boolean;
    href: string;
  }>;
  primaryContractId: string | null;
  commercialSummary: {
    name: string;
    client: string;
    value: number | null;
    currency: string;
    probability: number | null;
    stageLabel: string;
    owner: string;
    deadline: string | null;
  };
};

function isCurrentRow(row: Record<string, unknown>): boolean | null {
  const value = row.is_current ?? row.isCurrent;
  if (value == null || value === '') return null;
  return value === true || value === 1 || value === '1';
}

/** Prefer an explicit current row. Legacy rows with no flag stay first-wins. */
function pickCurrent(rows: Record<string, unknown>[]): Record<string, unknown> | undefined {
  const flagged = rows.filter((row) => isCurrentRow(row) === true);
  if (flagged.length) return flagged[0];
  if (rows.some((row) => isCurrentRow(row) === false)) return undefined;
  return rows[0];
}

function ownerLabel(opp: Opportunity | null): string {
  if (!opp) return '—';
  const u = opp.salesOwnerUser;
  if (u) return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '—';
  return opp.sales_owner || '—';
}

export function buildPursuitViewModel(input: {
  opportunityId: string;
  opportunity: Opportunity | null;
  context: CommercialLifecycleContext | null;
  definition: CommercialLifecycleDefinition | undefined;
  tenders: Record<string, unknown>[];
  contracts: Record<string, unknown>[];
  prequals?: Record<string, unknown>[];
}): PursuitWorkspaceViewModel {
  const { opportunityId, opportunity, context, definition, tenders, contracts, prequals = [] } = input;
  const stages = context ? buildLifecycleStages(context, definition) : [];

  const attention: PursuitAttentionItem[] = [];
  if (context?.blockedActions?.length) {
    for (const b of context.blockedActions) {
      attention.push({
        id: `blocked-${b.toStageKey}-${b.action}`,
        severity: 'warning',
        title: `Blocked: ${b.action}`,
        detail: b.reason,
        stageKey: b.toStageKey,
      });
    }
  }
  if (context?.currentStage?.requiresApproval) {
    attention.push({
      id: 'approval-pending',
      severity: 'critical',
      title: 'Approval pending',
      detail: `Stage ${context.currentStage.stageKey || 'current'} requires approval`,
      stageKey: context.currentStage.stageKey || undefined,
    });
  }
  if (context?.lifecycle?.status === 'unresolved') {
    attention.push({
      id: 'lifecycle-unresolved',
      severity: 'info',
      title: 'Commercial lifecycle not fully resolved',
      detail: (context.lifecycle.diagnostics || []).join(' ') || undefined,
    });
  }

  attention.push(...buildEarlyPursuitAttention({ opportunity, prequals, tenders }));

  const nextActions: PursuitNextAction[] = [];
  for (const a of context?.availableActions || []) {
    nextActions.push({
      id: `avail-${a.action}-${a.toStageKey}`,
      label: a.action,
      toStageKey: a.toStageKey,
      blocked: false,
      conversionProfileKey: a.conversionProfileKey,
      implementation: a.implementation,
    });
  }
  for (const b of context?.blockedActions || []) {
    nextActions.push({
      id: `block-${b.action}-${b.toStageKey}`,
      label: b.action,
      toStageKey: b.toStageKey,
      blocked: true,
      reason: b.reason,
    });
  }

  const lineage: PursuitLineageNode[] = (context?.lineage?.entities || []).map((e) => ({
    entityType: e.entityType,
    entityId: e.entityId,
    stageKey: e.stageKey,
    revision: e.revision,
  }));

  const currentTender = pickCurrent(tenders);
  const primaryTenderId =
    (currentTender?.id as string | undefined) ||
    lineage.find((n) => n.entityType === 'epc_tender')?.entityId ||
    null;

  const eoiRows = prequals.filter(
    (p) => epcInvitationKind(p.invitation_kind || p.invitationKind) === 'EOI'
  );
  const currentEoi = pickCurrent(eoiRows);
  const eoiPrequal = currentEoi || eoiRows[0];
  const primaryPrequalId =
    (eoiPrequal?.id as string | undefined) ||
    (pickCurrent(prequals)?.id as string | undefined) ||
    (prequals[0]?.id as string | undefined) ||
    lineage.find((n) => n.entityType === 'epc_prequalification')?.entityId ||
    null;

  const invitationKind =
    epcInvitationKind(currentTender?.invitation_kind || currentTender?.invitationKind) ||
    (currentEoi ? 'EOI' : eoiPrequal && !tenders.length ? 'EOI' : null);

  const invitationHistory = [
    ...eoiRows.map((row) => ({
      id: String(row.id),
      kind: 'EOI' as const,
      number: String(row.prequal_number || row.prequalNumber || ''),
      current: isCurrentRow(row) !== false && currentEoi?.id === row.id,
      href: `/epc/commercial/prequalifications/${row.id}`,
    })),
    ...tenders
      .map((row) => {
        const kind = epcInvitationKind(row.invitation_kind || row.invitationKind);
        if (kind !== 'RFQ' && kind !== 'RFT') return null;
        return {
          id: String(row.id),
          kind,
          number: String(row.tender_number || row.tenderNumber || ''),
          current: isCurrentRow(row) !== false && currentTender?.id === row.id,
          href: `/epc/commercial/tenders/${row.id}`,
        };
      })
      .filter((row): row is NonNullable<typeof row> => Boolean(row)),
  ];

  const primaryContractId =
    lineage.find((n) => n.entityType === 'epc_contract')?.entityId ||
    (contracts.find((c) => String(c.opportunity_id || '') === opportunityId)?.id as
      | string
      | undefined) ||
    null;

  const currentStage = stages.find((s) => s.status === 'current' || s.status === 'approval_pending');

  return {
    opportunityId,
    opportunity,
    context,
    stages,
    attention,
    nextActions,
    lineage,
    primaryTenderId,
    primaryPrequalId,
    invitationKind,
    invitationHistory,
    primaryContractId,
    commercialSummary: {
      name: opportunity?.opportunity_name || opportunity?.opportunity_number || 'Pursuit',
      client: opportunity?.customer_name || '—',
      value: opportunity?.estimated_value ?? opportunity?.expected_revenue ?? null,
      currency: opportunity?.currency || 'AED',
      probability: opportunity?.probability ?? null,
      stageLabel: currentStage?.label || opportunity?.stage || '—',
      owner: ownerLabel(opportunity),
      deadline: opportunity?.expected_close_date || null,
    },
  };
}

/** Map ECLE stage / entity to in-app navigation path when possible. */
export function stageFocusPath(
  stageKey: string,
  vm: PursuitWorkspaceViewModel
): string | null {
  if (vm.invitationKind === 'RFQ' && vm.primaryTenderId && stageKey.includes('ESTIM')) {
    return `/epc/commercial/tenders/${vm.primaryTenderId}?tab=estimate`;
  }
  if (stageKey.includes('TENDER') && vm.primaryTenderId) {
    return `/epc/commercial/tenders/${vm.primaryTenderId}`;
  }
  const node = vm.lineage.find((n) => n.stageKey === stageKey);
  const entityType = node?.entityType;
  const entityId = node?.entityId;

  if (entityType === 'epc_tender' && entityId) {
    return `/epc/commercial/tenders/${entityId}`;
  }
  if (entityType === 'epc_contract' && entityId) {
    return `/epc/commercial/contracts/${entityId}`;
  }
  if (entityType === 'epc_award' && entityId) {
    return `/epc/commercial/awards`;
  }
  if (entityType === 'epc_bid_decision') {
    return `/epc/commercial/bid-decisions`;
  }
  if (stageKey.includes('PREQUAL')) {
    return vm.primaryPrequalId
      ? `/epc/commercial/prequalifications/${vm.primaryPrequalId}`
      : `/epc/commercial/prequalifications`;
  }
  if (stageKey === 'LEAD' && vm.opportunity?.lead_id) {
    return `/sales/leads/${vm.opportunity.lead_id}`;
  }
  if (stageKey.includes('OPPORTUNITY') || stageKey === 'OPPORTUNITY') {
    return `/sales/opportunities/${vm.opportunityId}`;
  }
  if (entityType === 'epc_mobilization') {
    return `/epc/commercial/mobilizations`;
  }
  if (entityType === 'opportunity') {
    return `/sales/opportunities/${vm.opportunityId}`;
  }
  if (stageKey.includes('CONTRACT') && vm.primaryContractId) {
    return `/epc/commercial/contracts/${vm.primaryContractId}`;
  }
  return null;
}
